// How much of each catalogue colour the printer has used, derived from the
// EL-ement Statistics job history.
//
// A recorded print says what it used as a *source*: the material Bambu Studio
// reported ("PLA"), the preset's filament ID ("GFA00"), the AMS colour
// ("#6F5034FF") and a slicer estimate in grams. None of those is a catalogue
// key, and turning one into a key is the whole problem this module solves.
//
// Matching is deliberately conservative, because a gram credited to the wrong
// colour is worse than one left unmatched: it would tell you a spool is running
// low when a different one is.
//
//   1. A link the account made by hand always wins, including "don't track".
//   2. A filament ID this module *knows* names the line; the colour must then
//      match exactly within that line.
//   3. Otherwise the material and the exact colour must point at one catalogue
//      colour between them.
//   4. Everything else is unmatched, and listed so it can be linked.
//
// The known filament IDs are only the ones proven by real print history
// (2026-09-16, 52 jobs): GFA00 appears only with colours that exist only in PLA
// Basic, and GFA01 only with a colour that exists only in PLA Matte. Bambu has
// many more IDs, but an unverified guess here would misattribute usage silently
// -- an ID absent from this table falls through to rule 3 and costs nothing
// worse than a link to make.
//
// Rule 2 does not fall back to rule 3. A PLA Basic preset printing a colour
// only PLA Matte sells is a disagreement between two facts, and the person who
// loaded the spool is the one who can settle it.
//
// Pure, and shared: the server computes it and the browser renders its types.
import { FILAMENT_CATALOG, FILAMENT_LINES, filamentByKey } from './bambuFilaments.ts'
import type { FilamentColor } from './bambuFilaments.ts'
import type { ElementJob } from './elementStatistics.ts'
// Type-only, and erased at build: filamentStock.ts imports this module at runtime.
import type { AmsStock } from './filamentStock.ts'

/** Bambu Studio preset filament IDs proven against real print history. */
export const KNOWN_FILAMENT_IDS: Readonly<Record<string, string>> = Object.freeze({
  GFA00: 'bambu-lab/pla/basic',
  GFA01: 'bambu-lab/pla/matte',
})

/**
 * What a print reported, normalised so that equal filaments compare equal.
 * Unreported fields are the empty string rather than null, so the triple can be
 * a database key.
 */
export interface FilamentSource {
  /** Upper-case, e.g. "PLA". */
  material: string
  /** Upper-case, e.g. "GFA00". */
  filamentId: string
  /** `#RRGGBB`, upper-case, alpha dropped. */
  color: string
}

export interface FilamentUsageTotals {
  /** Slicer-estimated grams across every recorded print, failed ones included. */
  grams: number
  /** The part of `grams` from prints that failed or were aborted. */
  failedGrams: number
  /** Distinct prints that used it. */
  prints: number
  /** Start time of the most recent of those prints, or null if none was dated. */
  lastUsedAt: string | null
}

/** One recorded filament that was credited to a colour, and whether by hand. */
export interface SourceUsage extends FilamentSource {
  grams: number
  prints: number
  /** True when a link made by hand put it here; false when it matched on its own. */
  linked: boolean
}

export interface ColorUsage extends FilamentUsageTotals {
  key: string
  /**
   * What the prints reported, per filament, heaviest first -- so a colour can
   * say where its grams came from, and one source can be set aside without
   * waiting for it to be unmatched.
   */
  sources: SourceUsage[]
}

export interface UnmatchedUsage extends FilamentSource, FilamentUsageTotals {
  /** Catalogue keys most likely to be what this is, nearest colour first. */
  candidates: string[]
}

export type UntrackedUsage = FilamentSource & FilamentUsageTotals

/** A link made by hand. `key: null` is "don't track this filament". */
export interface FilamentUsageMapping {
  source: FilamentSource
  key: string | null
}

/**
 * What the usage endpoint returns: usage from the job history, and the AMS as
 * the printer last reported it.
 */
export interface FilamentUsageReport extends FilamentUsage {
  /** Null when no printer is connected or its AMS has never been reported. */
  ams: AmsStock | null
}

export interface FilamentUsage {
  colors: ColorUsage[]
  unmatched: UnmatchedUsage[]
  untracked: UntrackedUsage[]
  mappings: FilamentUsageMapping[]
  coverage: {
    /** Recorded prints that reported at least one filament. */
    prints: number
    /** Start of the earliest of them. */
    since: string | null
    grams: number
    /** Filament entries with no weight estimate, which count as no grams. */
    unweighedEntries: number
  }
}

const HEX = /^#?([0-9a-f]{6})(?:[0-9a-f]{2})?$/i

/** `#RRGGBB` from `#RRGGBBAA`, `RRGGBB`, any case. Empty if it is not a colour. */
export function normalizeColor(value: string | null | undefined): string {
  const match = HEX.exec((value ?? '').trim())
  return match ? `#${match[1].toUpperCase()}` : ''
}

export const normalizeSource = (source: {
  material?: string | null; filamentId?: string | null; color?: string | null
}): FilamentSource => ({
  material: (source.material ?? '').trim().toUpperCase(),
  filamentId: (source.filamentId ?? '').trim().toUpperCase(),
  color: normalizeColor(source.color),
})

export const sourceId = (source: FilamentSource): string =>
  `${source.material} ${source.filamentId} ${source.color}`

const LINE_MATERIAL = new Map(FILAMENT_LINES.map(line => [
  `${line.brand}/${line.material}/${line.type}`, line.material.toUpperCase(),
]))

const colorsOfMaterial = (material: string): FilamentColor[] =>
  FILAMENT_CATALOG.filter(color => LINE_MATERIAL.get(color.line) === material)

/**
 * One colour out of several with the same hex. Bambu keeps a retired code
 * listed beside its replacement with an identical hex, and usage belongs to the
 * one you can still buy. Two current colours sharing a hex are a genuine
 * ambiguity and resolve to nothing.
 */
function single(candidates: readonly FilamentColor[]): FilamentColor | null {
  if (candidates.length === 1) return candidates[0]
  const current = candidates.filter(color => !color.discontinued)
  return current.length === 1 ? current[0] : null
}

/**
 * The colour a spool reports. A print and the AMS carry one colour per
 * filament, and a multi-colour spool reports its first: a PLA Basic Gradient
 * Ocean to Meadow (#307FE2 to #54FF9B) came through real print history as
 * #307FE2 (2026-09-18). So the first hex is what a source is compared against.
 */
export const reportedHex = (color: FilamentColor): string => color.hexes[0]

/** The catalogue colour a source is, by rules 2 and 3, or null. */
export function matchSource(source: FilamentSource): FilamentColor | null {
  if (!source.color) return null
  const line = KNOWN_FILAMENT_IDS[source.filamentId]
  if (line) {
    return single(FILAMENT_CATALOG.filter(color =>
      color.line === line && reportedHex(color) === source.color))
  }
  if (!source.material) return null
  return single(colorsOfMaterial(source.material).filter(color =>
    reportedHex(color) === source.color))
}

const rgb = (hex: string): [number, number, number] =>
  [1, 3, 5].map(index => parseInt(hex.slice(index, index + 2), 16)) as [number, number, number]

const distance = (a: string, b: string): number => {
  const [r1, g1, b1] = rgb(a)
  const [r2, g2, b2] = rgb(b)
  return Math.hypot(r1 - r2, g1 - g2, b1 - b2)
}

/** Nearest current colours for an unmatched source, narrowed by what it reported. */
export function candidatesFor(source: FilamentSource, limit = 5): string[] {
  const line = KNOWN_FILAMENT_IDS[source.filamentId]
  let pool = line
    ? FILAMENT_CATALOG.filter(color => color.line === line)
    : source.material ? colorsOfMaterial(source.material) : []
  if (pool.length === 0) pool = [...FILAMENT_CATALOG]
  pool = pool.filter(color => !color.discontinued)
  if (!source.color) return pool.slice(0, limit).map(color => color.key)
  return pool
    .map(color => ({ key: color.key, d: distance(source.color, color.hexes[0]) }))
    .sort((a, b) => a.d - b.d)
    .slice(0, limit)
    .map(entry => entry.key)
}

interface Accumulator extends FilamentUsageTotals {
  jobs: Set<string>
}

const emptyTotals = (): Accumulator => ({
  grams: 0, failedGrams: 0, prints: 0, lastUsedAt: null, jobs: new Set(),
})

const add = (into: Accumulator, job: ElementJob, grams: number): void => {
  into.grams += grams
  if (job.result === 'failed_or_aborted') into.failedGrams += grams
  into.jobs.add(`${job.connectionId} ${job.id}`)
  if (job.startedAt && (!into.lastUsedAt || job.startedAt > into.lastUsedAt)) {
    into.lastUsedAt = job.startedAt
  }
}

const totals = ({ jobs, ...rest }: Accumulator): FilamentUsageTotals => ({
  ...rest,
  grams: Math.round(rest.grams * 100) / 100,
  failedGrams: Math.round(rest.failedGrams * 100) / 100,
  prints: jobs.size,
})

/**
 * Credit every recorded filament entry to a catalogue colour, to "not tracked",
 * or to the unmatched list.
 *
 * Every result counts, failed and aborted prints included, at their full slicer
 * estimate. For a page whose purpose is "reorder before you run out", counting
 * a failed print as used errs on the side of reordering early; `failedGrams`
 * keeps that share visible rather than hiding it inside the total.
 */
export function computeFilamentUsage(
  jobs: readonly ElementJob[],
  mappings: readonly FilamentUsageMapping[],
): FilamentUsage {
  const links = new Map(mappings.map(mapping => [sourceId(mapping.source), mapping.key]))
  const colors = new Map<string, Accumulator>()
  const colorSources = new Map<string, Map<string, { source: FilamentSource; acc: Accumulator; linked: boolean }>>()
  const unmatched = new Map<string, { source: FilamentSource; acc: Accumulator }>()
  const untracked = new Map<string, { source: FilamentSource; acc: Accumulator }>()
  const counted = new Set<string>()
  let since: string | null = null
  let grams = 0
  let unweighedEntries = 0

  for (const job of jobs) {
    if (job.materials.length === 0) continue
    counted.add(`${job.connectionId} ${job.id}`)
    if (job.startedAt && (!since || job.startedAt < since)) since = job.startedAt

    for (const entry of job.materials) {
      const weight = entry.estimatedWeightGrams
      if (weight === null || !Number.isFinite(weight)) unweighedEntries += 1
      const entryGrams = weight !== null && Number.isFinite(weight) ? weight : 0
      grams += entryGrams

      const source = normalizeSource(entry)
      const id = sourceId(source)
      const linked = links.has(id) ? links.get(id) : undefined
      // A link to a key the catalogue no longer has is treated as absent rather
      // than crediting grams to a row nobody can see.
      const key = linked === undefined
        ? matchSource(source)?.key ?? null
        : linked === null ? null : filamentByKey(linked)?.key ?? null

      if (linked === null) {
        const bucket = untracked.get(id) ?? { source, acc: emptyTotals() }
        add(bucket.acc, job, entryGrams)
        untracked.set(id, bucket)
      } else if (key) {
        const acc = colors.get(key) ?? emptyTotals()
        add(acc, job, entryGrams)
        colors.set(key, acc)
        const sources = colorSources.get(key) ?? new Map()
        const bucket = sources.get(id) ?? { source, acc: emptyTotals(), linked: linked !== undefined }
        add(bucket.acc, job, entryGrams)
        sources.set(id, bucket)
        colorSources.set(key, sources)
      } else {
        const bucket = unmatched.get(id) ?? { source, acc: emptyTotals() }
        add(bucket.acc, job, entryGrams)
        unmatched.set(id, bucket)
      }
    }
  }

  const byGrams = <T extends { grams: number }>(a: T, b: T) => b.grams - a.grams

  return {
    colors: [...colors].map(([key, acc]) => ({
      key,
      ...totals(acc),
      sources: [...(colorSources.get(key)?.values() ?? [])]
        .map(({ source, acc: sourceAcc, linked }) => {
          const { grams: sourceGrams, prints } = totals(sourceAcc)
          return { ...source, grams: sourceGrams, prints, linked }
        })
        .sort(byGrams),
    })).sort(byGrams),
    unmatched: [...unmatched.values()]
      .map(({ source, acc }) => ({ ...source, ...totals(acc), candidates: candidatesFor(source) }))
      .sort(byGrams),
    untracked: [...untracked.values()]
      .map(({ source, acc }) => ({ ...source, ...totals(acc) }))
      .sort(byGrams),
    mappings: [...mappings],
    coverage: {
      prints: counted.size,
      since,
      grams: Math.round(grams * 100) / 100,
      unweighedEntries,
    },
  }
}
